import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const services = [
  {
    icon: 'ri-home-4-line',
    title: 'Heritage Painting',
    description: 'Specialized painting for Victorian and Edwardian homes',
    price: 'From R12,000',
    features: ['Period-appropriate colors', 'Heritage expertise', 'Restoration work', '3-year warranty']
  },
  {
    icon: 'ri-paint-brush-line',
    title: 'Residential Painting',
    description: 'Complete interior and exterior painting for modern homes',
    price: 'From R9,500',
    features: ['Premium finishes', 'Color consultation', 'Quality materials', '2-year warranty']
  },
  {
    icon: 'ri-building-line',
    title: 'Apartment Painting',
    description: 'Professional painting services for apartments and complexes',
    price: 'From R7,500',
    features: ['Efficient service', 'Minimal disruption', 'Clean work', 'Fast completion']
  }
];

const paintingProcess = [
  { step: '1', title: 'Assessment', description: 'Property evaluation and color consultation' },
  { step: '2', title: 'Preparation', description: 'Careful surface prep and protection' },
  { step: '3', title: 'Application', description: 'Expert painting with premium materials' },
  { step: '4', title: 'Inspection', description: 'Quality check and client walkthrough' }
];

const testimonials = [
  {
    rating: 5,
    text: 'Outstanding work on our Victorian home! They respected the heritage character while delivering modern quality. Truly exceptional craftsmanship.',
    name: 'Elizabeth Moore',
    property: 'Tamboerskloof Heritage Home',
    image: 'https://public.readdy.ai/ai/img_res/36431993d00c4b5516cbb1ed35da1cf4.jpg'
  },
  {
    rating: 5,
    text: 'Excellent service and attention to detail. The team was professional, knowledgeable, and delivered beautiful results. Highly recommend!',
    name: 'Andrew Stevens',
    property: 'Tamboerskloof Resident',
    image: 'https://readdy.ai/api/search-image?query=Professional%20South%20African%20man%20homeowner%20portrait%20smiling%2C%20natural%20lighting%2C%20realistic%20photography&width=100&height=100&seq=testimonial-tamboerskloof-002&orientation=squarish'
  },
  {
    rating: 5,
    text: 'Very impressed with the quality and professionalism. They transformed our apartment beautifully. Great communication throughout the project.',
    name: 'Rachel Cohen',
    property: 'Apartment Owner',
    image: 'https://readdy.ai/api/search-image?query=Professional%20South%20African%20woman%20portrait%20smiling%2C%20natural%20lighting%2C%20realistic%20photography&width=100&height=100&seq=testimonial-tamboerskloof-003&orientation=squarish'
  }
];

export default function TamboerskloofPage() {
  useEffect(() => {
    document.title = 'Tamboerskloof Painting Services | Professional Painters Cape Town';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert painting services in Tamboerskloof, Cape Town. Residential, heritage, and commercial painting solutions. Professional painters with 15+ years experience. Free quotes available.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Tamboerskloof painters, painting services Tamboerskloof, house painters Tamboerskloof, heritage painters Cape Town, residential painting');
    }

    const metaGeoPosition = document.querySelector('meta[name="geo.position"]');
    if (metaGeoPosition) {
      metaGeoPosition.setAttribute('content', '-33.9345;18.4089');
    }

    const metaGeoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (metaGeoPlacename) {
      metaGeoPlacename.setAttribute('content', 'Tamboerskloof, Cape Town, Western Cape, South Africa');
    }

    const metaGeoRegion = document.querySelector('meta[name="geo.region"]');
    if (metaGeoRegion) {
      metaGeoRegion.setAttribute('content', 'ZA-WC');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20historic%20home%20in%20Tamboerskloof%20Cape%20Town%20with%20Table%20Mountain%20backdrop%2C%20skilled%20painters%20on%20scaffolding%20applying%20paint%20to%20house%20walls%2C%20beautiful%20residential%20property%2C%20sunny%20day%2C%20professional%20equipment%2C%20high%20quality%20workmanship%2C%20realistic%20photography&width=1920&height=1080&seq=tamboerskloof-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Tamboerskloof
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Tamboerskloof's residential and commercial properties. Quality craftsmanship and reliable service for your home or business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Area Description */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Painting Experts Serving Tamboerskloof</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Tamboerskloof is one of Cape Town's most charming residential neighborhoods, nestled on the slopes of Table Mountain with stunning views and rich architectural heritage. Our professional painting services are tailored to preserve and enhance the character of Tamboerskloof painting services for historic homes, modern residences, and apartment buildings.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                With over 15 years of experience serving Tamboerskloof and the City Bowl, we specialize in both heritage restoration and contemporary painting projects. From Victorian and Edwardian homes to modern apartments, our team understands the unique requirements of properties in this prestigious hillside community.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you need interior painting for your Tamboerskloof home, exterior painting that respects heritage aesthetics, or specialized restoration work, our skilled painters combine traditional craftsmanship with modern techniques to deliver exceptional results that enhance your property's beauty and value.
              </p>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20team%20working%20on%20historic%20Victorian%20house%20exterior%20in%20Tamboerskloof%20Cape%20Town%2C%20showing%20skilled%20craftsmen%20applying%20paint%20with%20care%2C%20heritage%20architecture%2C%20quality%20painting%20equipment%2C%20mountain%20backdrop%2C%20professional%20service%20photography%20with%20natural%20lighting&width=600&height=500&seq=tamboerskloof-team-002&orientation=portrait"
                alt="Professional Painters in Tamboerskloof"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Tamboerskloof Painting Services</h2>
            <p className="text-xl text-gray-600">Specialized painting solutions for heritage and modern properties</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-black font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line mr-2" style={{color: '#ffff00'}}></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Tamboerskloof Residents Choose Us</h2>
            <p className="text-xl text-gray-600">Trusted experts in heritage and residential painting</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-award-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">15+ Years Experience</h3>
              <p className="text-gray-600">Extensive experience with Tamboerskloof heritage properties</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-ancient-gate-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Heritage Expertise</h3>
              <p className="text-gray-600">Specialized in historic home restoration</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-team-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Skilled Craftsmen</h3>
              <p className="text-gray-600">Expert painters with attention to detail</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-check-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Quality Guarantee</h3>
              <p className="text-gray-600">Premium materials and workmanship warranty</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Painting Process</h2>
            <p className="text-xl text-gray-600">Professional service with respect for your property</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Recent Projects in Tamboerskloof</h2>
            <p className="text-xl text-gray-600">Excellence in heritage and modern painting</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Restored%20Victorian%20house%20exterior%20with%20fresh%20heritage%20paint%20colors%20in%20Tamboerskloof%20Cape%20Town%2C%20showing%20ornate%20architectural%20details%2C%20period-appropriate%20color%20scheme%2C%20mountain%20backdrop%2C%20professional%20restoration%20painting%20work%2C%20natural%20lighting&width=400&height=300&seq=tamboerskloof-project-001&orientation=landscape"
                  alt="Victorian Home Restoration"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Victorian Restoration</h3>
              <p className="text-gray-600">Heritage home exterior with period-appropriate colors</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20apartment%20interior%20with%20freshly%20painted%20walls%20in%20sophisticated%20neutral%20tones%20in%20Tamboerskloof%2C%20showing%20contemporary%20design%2C%20clean%20paint%20finish%2C%20city%20views%20through%20windows%2C%20professional%20interior%20painting%20work&width=400&height=300&seq=tamboerskloof-project-002&orientation=landscape"
                  alt="Modern Apartment Interior"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Modern Apartment</h3>
              <p className="text-gray-600">Contemporary interior with designer finishes</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Edwardian%20house%20exterior%20with%20beautifully%20restored%20painted%20facade%20in%20Tamboerskloof%2C%20showing%20classic%20architecture%2C%20fresh%20paint%20in%20heritage%20colors%2C%20well-maintained%20property%2C%20professional%20painting%20work%2C%20golden%20hour%20lighting&width=400&height=300&seq=tamboerskloof-project-003&orientation=landscape"
                  alt="Edwardian Home Painting"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Edwardian Home</h3>
              <p className="text-gray-600">Complete exterior painting with heritage respect</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Residential%20interior%20living%20space%20with%20freshly%20painted%20walls%20in%20elegant%20colors%20in%20Tamboerskloof%20home%2C%20showing%20period%20features%2C%20modern%20comfort%2C%20quality%20paint%20finish%2C%20natural%20lighting%2C%20professional%20interior%20work&width=400&height=300&seq=tamboerskloof-project-004&orientation=landscape"
                  alt="Interior Renovation"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Interior Renovation</h3>
              <p className="text-gray-600">Complete interior painting with elegant finishes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Tamboerskloof Clients Say</h2>
            <p className="text-xl text-gray-600">Trusted by Tamboerskloof homeowners</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-lg" style={{color: '#ffff00'}}></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{backgroundColor: '#ffff00'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Transform Your Tamboerskloof Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Tamboerskloof's trusted painting professionals. Get a free quote for your residential or commercial property.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}